import { useState, useMemo } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { useFetchAutocompleteOptionsQuery } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Autocomplete, Box, Chip, SxProps, TextField, Theme } from '@mui/material';
import { Controller, Control, ControllerRenderProps } from 'react-hook-form';

import { ProfileFieldConfig } from './types/profile.types';

type ProfileAutoCompleteFieldProps = Readonly<{
  field: ProfileFieldConfig;
  control: Control;
  disabled?: boolean;
}>;

export const ProfileApiAutoCompleteField = ({
  field,
  control,
  disabled,
}: ProfileAutoCompleteFieldProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: autocompleteResults } = useFetchAutocompleteOptionsQuery(
    {
      endpoint: field.type.variant === 'API_AUTOCOMPLETE' ? field.type.autoCompleteEndpoint : '',
      searchTerm,
    },
    {
      skip: !searchTerm || searchTerm.length < 4 || field.type.variant !== 'API_AUTOCOMPLETE',
    },
  );

  const formattedOptions = useMemo(() => {
    if (!autocompleteResults?.length) return [];

    return autocompleteResults[0]?.values ?? [];
  }, [autocompleteResults]);

  const initialValue = () => {
    if (typeof field.defaultValue === 'string') {
      return { label: field.defaultValue, value: field.defaultValue };
    }

    if (Array.isArray(field.defaultValue)) {
      return field.defaultValue.map((value) => ({ label: value, value }));
    }

    return null;
  };

  const handleOnChange = (newValue: string | string[], controllerField: ControllerRenderProps) => {
    setSearchTerm('');
    const parsed = Array.isArray(newValue)
      ? newValue.map((val) => (typeof val === 'string' ? { label: val, value: val } : val))
      : typeof newValue === 'string'
        ? { label: newValue, value: newValue }
        : newValue;

    controllerField.onChange(parsed);

    if (Array.isArray(parsed)) {
      field.onChange?.(parsed.map((opt) => opt.value));

      return;
    }

    field.onChange?.([parsed?.value ?? '']);
  };

  return (
    <Box sx={autoCompleteSelectContainerSx} key={field.key}>
      <Controller
        name={field.key}
        control={control}
        defaultValue={initialValue()}
        render={({ field: controllerField, fieldState: { error } }) => {
          const value = controllerField.value ?? initialValue() ?? '';

          const mergedOptions =
            value && !formattedOptions.some((opt) => opt.value === value.value)
              ? [value, ...formattedOptions]
              : formattedOptions;

          const isMulti = field.maxSelectedOptions !== 1;

          return (
            <>
              <Autocomplete
                freeSolo
                autoHighlight
                options={mergedOptions}
                value={value}
                sx={autoCompleteSx}
                disableClearable={isMulti}
                multiple={isMulti}
                disabled={disabled}
                popupIcon={<AppIcon iconName="ChevronDown" diameter="20px" color="Gray/400" />}
                onChange={(_, newValue) => handleOnChange(newValue, controllerField)}
                onInputChange={(_, input, reason) => {
                  if (reason === 'input') {
                    setSearchTerm(input);
                    if (!isMulti && input === '') {
                      controllerField.onChange(null);
                      field.onChange?.(['']);
                    }
                  }
                }}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : (option.label ?? option.value)
                }
                open={searchTerm.length >= 4}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={undefined}
                    error={!!error}
                    helperText={isMulti ? 'Minimum 4 characters' : undefined}
                    placeholder={field.placeholder}
                    sx={textFieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{params.InputProps.endAdornment}</>,
                      startAdornment: field.showSearch ? (
                        <Box sx={searchIconSx}>
                          <AppIcon diameter="24px" iconName="SearchMd" color="Gray/500" />
                        </Box>
                      ) : null,
                    }}
                    inputProps={{
                      ...params.inputProps,
                      onKeyDown: (e) => {
                        // prevent chip removal on backspace only in multi-mode
                        if (e.key === 'Backspace' && isMulti && !searchTerm) {
                          e.stopPropagation();
                        }
                      },
                      onBlur: (e) => {
                        setSearchTerm('');
                        if (!isMulti) {
                          handleOnChange(e.target.value, controllerField);
                        }
                      },
                    }}
                  />
                )}
                renderOption={(props, option, { selected }) => {
                  return (
                    <Box component="li" {...props} sx={menuItemSx}>
                      <Box width="100%">{option.label}</Box>

                      {selected && (
                        <AppIcon
                          iconName="Check"
                          diameter="20px"
                          color="Colors/Foreground/fg-brand-primary"
                        />
                      )}
                    </Box>
                  );
                }}
              />

              {isMulti && Array.isArray(value) && (
                <Box sx={underneathTagContainerSx}>
                  {value.map((option, index) => (
                    <Chip
                      key={`${field.key}-${option.value}-${index}`}
                      label={
                        option.label.length > 40 ? option.label.slice(0, 40) + '...' : option.label
                      }
                      variant="tag"
                      size="md"
                      sx={chipSx}
                      onDelete={() => {
                        const newValue = value.filter((_, idx) => idx !== index);

                        controllerField.onChange(newValue);
                        field.onChange?.(newValue.map((opt) => opt.value));
                      }}
                      deleteIcon={
                        <Box sx={deleteIconContainerStyle}>
                          <AppIcon
                            iconName="XClose"
                            diameter="12px"
                            color="Colors/Foreground/fg-quaternary"
                          />
                        </Box>
                      }
                    />
                  ))}
                </Box>
              )}
            </>
          );
        }}
      />
    </Box>
  );
};

const autoCompleteSelectContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  width: '100%',
  maxWidth: { xs: '70vw', md: '100%' },
};

const underneathTagContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
};

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-primary'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const deleteIconContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  margin: `${backrs.spacing['spacing-none']} !important`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const searchIconSx: SxProps<Theme> = ({ backrs }) => ({
  paddingX: { xs: backrs.spacing['spacing-none'] },
  display: 'flex',
});

const autoCompleteSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-sm'],

  '& .MuiAutocomplete-root': {
    paddingY: `${backrs.spacing['spacing-none']} !important`,
  },
  '& .MuiAutocomplete-inputRoot': {
    border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    paddingY: `${backrs.spacing['spacing-none']} !important`,
    paddingX: { xs: `${backrs.spacing['spacing-sm']} !important` },

    gap: backrs.spacing['spacing-xs'],
    maxHeight: '40px',
    minHeight: '40px',
    height: '40px',
  },

  '& .MuiAutocomplete-endAdornment': {
    top: '24px',
  },
  '& .MuiAutocomplete-tag': {
    margin: `${backrs.spacing['spacing-none']}`,
  },
  '& .MuiAutocomplete-popupIndicator': {
    transition: 'transform 0.3s ease',
  },
});

const menuItemSx: SxProps<Theme> = ({ backrs }) => ({
  transition: 'all 0.3s ease-in-out',

  '&.Mui-focused, &:hover, &.Mui-focusVisible, &.Mui-selected, &.Mui-active, &.MuiAutocomplete-option[aria-selected="true"], &[data-focus="true"]':
    {
      backgroundColor: `${backrs.colors['Colors/Background/bg-active']} !important`,
    },
});

const textFieldSx: SxProps<Theme> = {
  // we don't want to show the X, but we need to enable it for the clearable prop to work
  '& .MuiAutocomplete-clearIndicator': {
    visibility: 'hidden !important',
    display: 'none !important',
    color: 'transparent',
    cursor: 'default',
    '&:hover': {
      color: 'transparent',
      backgroundColor: 'transparent',
    },
  },
};
